import React, { useState } from 'react';
import { Eye, X, Loader2 } from 'lucide-react';
import { ServiceVariations } from './VariationChip';
import { buildServiceImages } from '../utils/imageUtils';

const ServiceDetailsBottomSheet = ({ 
  service, 
  variations,
  isVisible, 
  onClose, 
  onAddService, 
  onRemoveService, 
  isSelected, 
  selectedVariations = {}, 
  onVariationSelect,
  onVariationToggle,
  serviceSubjectToEvaluation = false
}) => {
  const [selectedImageForModal, setSelectedImageForModal] = useState(null);
  const [imageModalVisible, setImageModalVisible] = useState(false);

  if (!isVisible || !service) return null;

  const serviceImages = buildServiceImages(service);

  const formatDuration = (minutes) => {
    if (minutes === undefined || minutes === null || minutes <= 0) return '-';
    const h = Math.floor(minutes / 60);
    const m = minutes % 60;
    return `${h > 0 ? h + 'h ' : ''}${m > 0 ? m + 'min' : ''}`.trim() || '-';
  };

  const formatPrice = (priceStr) => {
    if (priceStr === undefined || priceStr === null) return 'R$ -';
    const priceNum = parseFloat(priceStr);
    if (isNaN(priceNum)) return 'R$ -';
    return `R$ ${priceNum.toFixed(2).replace('.', ',')}`;
  };

  const handleImagePress = (imageUrl) => {
    setSelectedImageForModal(imageUrl);
    setImageModalVisible(true);
  };

  const handleImageModalClose = () => {
    setImageModalVisible(false);
    setTimeout(() => setSelectedImageForModal(null), 300); // Wait for animation before clearing
  };

  return (
    <>
      {/* Main Bottom Sheet - Luxury Design */}
      <div
        className={`fixed inset-0 bg-brand-dark/90 dark:bg-black/95 backdrop-blur-sm z-50 transition-opacity duration-300 ${
          isVisible ? 'opacity-100' : 'opacity-0 pointer-events-none'
        }`}
        onClick={onClose}
      >
        {/* Bottom Sheet Container */}
        <div
          className={`fixed bottom-0 left-0 right-0 bg-brand-light dark:bg-brand-dark border-t border-brand-dark/10 dark:border-brand-light/10 z-50 max-h-[90vh] overflow-hidden transform transition-transform duration-300 ${
            isVisible ? 'translate-y-0' : 'translate-y-full'
          }`}
          onClick={(e) => e.stopPropagation()}
        >
          {/* Draggable Handle - Minimal square design */}
          <div className="w-16 h-1 bg-brand-dark/20 dark:bg-brand-light/20 mx-auto my-4"></div>

          {/* Close Button */}
          <div className="absolute top-4 right-4 z-10">
            <button
              onClick={onClose}
              className="p-2 hover:bg-brand-dark/5 dark:hover:bg-brand-light/5 transition-all duration-300"
              aria-label="Fechar detalhes do serviço"
            >
              <X size={22} className="text-brand-dark dark:text-brand-light" strokeWidth={1.5} />
            </button>
          </div>

          {/* Scrollable Content */}
          <div className="overflow-y-auto h-[calc(90vh-60px)] pb-24">
            {/* Service Images Carousel - Luxury Magazine Style */}
            {serviceImages.length > 0 && (
              <div className="px-6 pt-4 pb-6">
                <div className="flex space-x-3 overflow-x-auto pb-4 scrollbar-hide">
                  {serviceImages.map((image, index) => (
                    <div key={index} className="flex-shrink-0 relative group">
                      <img
                        src={image.src}
                        alt=""
                        className="w-44 h-44 object-cover cursor-pointer hover:opacity-90 transition-opacity duration-300"
                        onClick={() => handleImagePress(image.src)}
                      />
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Service Name - Serif Font */}
            <div className="px-6 py-3 border-t border-brand-dark/10 dark:border-brand-light/10">
              <h2 className="text-2xl font-serif font-bold text-brand-dark dark:text-brand-light tracking-wide">{service.name}</h2>
            </div>

            {/* Service Details */}
            <div className="px-6 py-4 space-y-6">
              {/* Description */}
              {service.description && (
                <div>
                  <div
                    className="text-brand-dark dark:text-brand-light text-sm leading-relaxed prose prose-sm max-w-none"
                    dangerouslySetInnerHTML={{ __html: service.description }}
                  />
                </div>
              )}

          
              {/* Service Selection */}
              {variations && Array.isArray(variations) && variations.length > 0 ? (
                <ServiceVariations
                  variationGroups={variations}
                  selectedVariations={selectedVariations}
                  onVariationSelect={(groupId, variation) => {
                    // Check if this is a multiple choice group
                    const group = variations.find(g => g.id === groupId);
                    if (group && group.multiple_choice) {
                      // For multiple choice, use toggle
                      onVariationToggle(service.id, groupId, variation);
                    } else {
                      // For single choice, use select
                      onVariationSelect(service.id, groupId, variation);
                    }
                  }}
                  onVariationToggle={(groupId, variation) => {
                    // For multiple choice groups
                    onVariationToggle(service.id, groupId, variation);
                  }}
                  size="small"
                  showGroupNames={true}
                  basePrice={parseFloat(service.price)}
                  baseDuration={service.duration_minutes || 0}
                  serviceSubjectToEvaluation={serviceSubjectToEvaluation}
                />
              ) : (
                // Service without variations - show base service option
                <ServiceVariations
                  variationGroups={[{
                    id: 'base',
                    name: 'Base Service',
                    service_id: service.id,
                    variations: [{
                      id: 'base',
                      name: service.name,
                      price_delta: 0,
                      duration_delta: 0,
                      display_order: 0,
                      price_subject_to_evaluation: service.price_subject_to_evaluation || false,
                      service_variation_group_id: 'base'
                    }]
                  }]}
                  selectedVariations={isSelected ? { 'base': { id: 'base' } } : {}}
                  onVariationSelect={(groupId, variation) => {
                    // Handle base service selection - toggle on/off
                    if (variation === null && isSelected) {
                      onRemoveService(); // Unselect
                    } else if (variation !== null && !isSelected) {
                      onAddService(); // Select
                    }
                  }}
                  onVariationToggle={() => {}} // Not used for base services
                  size="small"
                  showGroupNames={false}
                  basePrice={parseFloat(service.price)}
                  baseDuration={service.duration_minutes || 0}
                  serviceSubjectToEvaluation={serviceSubjectToEvaluation}
                />
              )}

              {/* Action Button - Luxury Design */}
              {!variations.some(group => group.multiple_choice) && (
                <button
                  onClick={() => {
                    if (isSelected) {
                      onRemoveService();
                    } else {
                      onAddService();
                    }
                  }}
                  className={`w-full py-4 px-6 border font-medium text-sm tracking-widest uppercase transition-all duration-300 ${
                    isSelected
                      ? 'bg-transparent border-red-600 dark:border-red-400 text-red-600 dark:text-red-400 hover:bg-red-600 hover:dark:bg-red-400 hover:text-white hover:dark:text-black'
                      : 'bg-brand-dark dark:bg-brand-light border-brand-dark dark:border-brand-light text-brand-light dark:text-brand-dark hover:bg-brand-dark/90 dark:hover:bg-brand-light/90'
                  }`}
                >
                  {isSelected ? 'Remover Serviço' : 'Adicionar Serviço'}
                </button>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Image Modal - Luxury Design */}
      {imageModalVisible && (
        <div
          className="fixed inset-0 bg-brand-dark/95 dark:bg-black/95 flex items-center justify-center z-[60] backdrop-blur-sm"
          onClick={handleImageModalClose}
        >
          <div className="relative max-w-2xl max-h-[80vh] m-4">
            <button
              onClick={handleImageModalClose}
              className="absolute -top-12 right-0 p-3 text-brand-light hover:bg-brand-light/10 transition-all duration-300"
            >
              <X size={24} strokeWidth={1.5} />
            </button>
            {selectedImageForModal && (
              <img
                src={selectedImageForModal}
                alt="Service"
                className="w-full h-full object-contain"
              />
            )}
          </div>
        </div>
      )}
    </>
  );
};

export default ServiceDetailsBottomSheet;